import axios from "axios";
import { QuestionData, WordListRequestBody, WordJSON } from "./interfaces";
import { getQuestionsArr } from "../ts/questionBuilders";
import { decodeText } from "../ts/textDecode";
import { apiURI, DEBUG, ENVIRONMENT } from "./environment";

function formatLangName(str: string) {
  str = str.replace("-", " ");

  let words = str.split(" ");
  words.forEach((word, i) => {
    if (word !== "language" && word !== "the") {
      words[i] = word[0].toUpperCase() + word.slice(1);
    }
  });

  return words.join(" ");
}

function quoteIfNeeded(s?: string): string {
  const t = (s ?? "").trim();
  if (!t) return '""';
  const pairs: Array<[string, string]> = [
    ['"', '"'],
    ["“", "”"],
    ["‘", "’"],
    ["'", "'"],
  ];
  for (const [L, R] of pairs) {
    if (t.startsWith(L) && t.endsWith(R)) return t;
  }
  return `"${t}"`;
}

// Normalize phrases so DB phrases and options line up
function normalizePhrase(s: string): string {
  return decodeText(s || "").trim();
}

function formatDateTime() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}

function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  if (link.download !== undefined) {
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

function downloadText(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  if (link.download !== undefined) {
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

//escape quotes and wrap every field
function csvEscape(value: string): string {
  const v = value ?? "";
  const escaped = v.replace(/"/g, '""');
  return `"${escaped}"`;
}

const PHRASE_METADATA_COLUMNS: Array<{ header: string; field: string }> = [
  { header: "difficulty", field: "difficulty" },
  { header: "researched_date", field: "researched_date" },
  { header: "language_of_origin", field: "language_of_origin" },
  { header: "origin_exclude", field: "origin_exclude" },
  { header: "phrasetype", field: "subcat" },
  { header: "phrasetype_exclude", field: "subcat_exclude" },
  { header: "author", field: "author" },
];

// Fetch all phrases + metadata dynamically from the API
async function fetchAllPhrasesFromDB(): Promise<{
  phrases: string[];
  metadataByPhrase: { [phrase: string]: WordJSON };
}> {
  const body: WordListRequestBody = {
    debug: DEBUG,
    uuid: "gametester-phrases",
    number: 5000,
    random_cat: false,
  };

  const axiosConfig =
    ENVIRONMENT !== "production"
      ? { auth: { username: "igins", password: "llc" } }
      : {};

  const response = await axios.post(
    apiURI + "/wordsupdated/list2",
    body,
    axiosConfig
  );

  const data = response.data.data as { [id: string]: WordJSON } | WordJSON[];

  const phrases: string[] = [];
  const metadataByPhrase: { [phrase: string]: WordJSON } = {};
  const values = Array.isArray(data) ? (data as WordJSON[]) : Object.values(data);

  values.forEach((word) => {
    if (word && word.phrase) {
      const norm = normalizePhrase(word.phrase);
      phrases.push(norm);
      metadataByPhrase[norm] = word;
    }
  });
  console.log("metadataByPhrase", metadataByPhrase);
  return { phrases, metadataByPhrase };
}

function getQuestionText(
  allQuestionData: QuestionData[],
  difficulty: string,
  index: number
) {
  const { type, subject, subjectLangs, range, article, subcat, author } =
    allQuestionData[index];
  const currentDifficulty = difficulty;

  if (type === "origin-include" && subjectLangs && subjectLangs.length) {
    const langs = subjectLangs.map((lang) => formatLangName(lang));
    switch (langs.length) {
      case 1:
        return "Which phrase comes from " + langs[0] + "?";
      case 2:
        return "Which phrase comes from " + langs[0] + " and " + langs[1] + "?";
      default: {
        const allButLast = langs.slice(0, -1).join(", ");
        const last = langs[langs.length - 1];
        return (
          "Which phrase comes from languages including " +
          allButLast +
          ", and " +
          last +
          "?"
        );
      }
    }
  } else if (type === "origin-exclude" && subjectLangs && subjectLangs.length) {
    const langs = subjectLangs.map((lang) => formatLangName(lang));
    switch (langs.length) {
      case 1:
        return "Which phrase does NOT come from " + langs[0] + "?";
      case 2:
        return (
          "Which phrase does NOT come from " +
          langs[0] +
          " and " +
          langs[1] +
          "?"
        );
      default: {
        const allButLast = langs.slice(0, -1).join(", ");
        const last = langs[langs.length - 1];
        return (
          "Which phrase does NOT come from languages including " +
          allButLast +
          ", and " +
          last +
          "?"
        );
      }
    }
  } else if (type === "origin-multi") {
    switch (currentDifficulty) {
      case "moderate":
        return "Which two languages does " + quoteIfNeeded(subject) + " come from?";
      case "hard": {
        const quoted = quoteIfNeeded(subject);
        const skipQ = /[\?\uFF1F]\s*$/.test(decodeText(subject || "")) ? "" : "?";
        return (
          "From which of these languages does modern English get the phrase " +
          quoted +
          skipQ
        );
      }
      default:
        return "Which language does " + quoteIfNeeded(subject) + " come from?";
    }
  } else if (type === "subcat-include") {
    // Text for subcat-include questions
    if (subcat === "advertising") {
      return "Which of these phrases HAS been used in advertising?";
    } else if (subcat === "bible") {
      return "Which of these phrases HAS been used in relation to the Bible?";
    } else if (subcat === "book") {
      return "Which of these phrases HAS been used in relation to a book?";
    } else if (subcat === "device") {
      return "Which of these phrases HAS been used in relation to a device?";
    } else if (subcat === "letter") {
      return "Which of these phrases HAS been used in relation to a letter?";
    } else if (subcat === "magazine-newspaper") {
      return "Which of these phrases HAS been used in magazine/newspaper?";
    } else if (subcat === "movies-tv") {
      return "Which of these phrases HAS been used in Movies/TV?";
    } else if (subcat === "music") {
      return "Which of these phrases HAS been used in music vernacular?";
    } else if (subcat === "place") {
      return "Which of these phrases HAS been used in relation to a place name?";
    } else if (subcat === "speech") {
      return "Which of these phrases HAS been used in speech?";
    } else if (subcat === "sports") {
      return "Which of these phrases HAS been used in sports?";
    } else if (subcat === "theater") {
      return "Which of these phrases HAS been used in theater?";
    } else if (article) {
      return (
        "Which of these phrases HAS been used in relation to " +
        article.slice(1, -1) +
        " " +
        subcat +
        "?"
      );
    } else {
      return "Which of these phrases HAS been used in " + subcat + "?";
    }
  } else if (type === "subcat-exclude") {
    // Text for subcat-exclude questions
    if (subcat === "advertising") {
      return "Which of these phrases has NOT been used in advertising?";
    } else if (subcat === "bible") {
      return "Which of these phrases has NOT been used in relation to the Bible?";
    } else if (subcat === "book") {
      return "Which of these phrases has NOT been used in relation to a book?";
    } else if (subcat === "device") {
      return "Which of these phrases has NOT been used in relation to a device?";
    } else if (subcat === "letter") {
      return "Which of these phrases has NOT been used in relation to a letter?";
    } else if (subcat === "magazine-newspaper") {
      return "Which of these phrases has NOT been used in magazine/newspaper?";
    } else if (subcat === "movies-tv") {
      return "Which of these phrases has NOT been used in Movies/TV?";
    } else if (subcat === "music") {
      return "Which of these phrases has NOT been used in music vernacular?";
    } else if (subcat === "place") {
      return "Which of these phrases has NOT been used in relation to a place name?";
    } else if (subcat === "speech") {
      return "Which of these phrases has NOT been used in speech?";
    } else if (subcat === "sports") {
      return "Which of these phrases has NOT been used in sports?";
    } else if (subcat === "theater") {
      return "Which of these phrases has NOT been used in theater?";
    } else if (article) {
      return (
        "Which of these phrases has NOT been used in relation to " +
        article.slice(1, -1) +
        " " +
        subcat +
        "?"
      );
    } else {
      return "Which of these phrases has NOT been used in " + subcat + "?";
    }
  } else {
    // Prefix "the" when options will be decades or centuries e.g. "the 1910s" or "the 19th century"
    let rangePrefix = "";

    if (range && range.endsWith("century")) {
      rangePrefix = "the ";
    }

    // All other question types
    switch (type) {
      case "date":
        return (
          "When is the phrase " +
          quoteIfNeeded(subject) +
          " first recorded in English?"
        );
      case "date-range-include":
        return (
          "Which of the following phrases IS first recorded in " +
          rangePrefix +
          range +
          "?"
        );
      case "date-range-exclude":
        return (
          "Which of the following phrases is NOT first recorded in " +
          rangePrefix +
          range +
          "?"
        );
      case "order":
        return "Order the following phrases descending by date of first use (First used phrase on top):";
      case "first":
        return "Which of the following phrases WAS the first to appear in English?";
      case "last":
        return "Which of the following phrases WAS the last to appear in English?";
      case "subcat-multi":
        return "The phrase " + quoteIfNeeded(subject) + " comes from: ";
      case "author-include":
        return "Which of these phrases is coined by " + author + "?";
      case "author-exclude":
        return "Which of these phrases is NOT coined by " + author + "?";
      case "author-origin": {
        const quoted = quoteIfNeeded(subject);
        const skipQ = /[\?\uFF1F]\s*$/.test(decodeText(subject || "")) ? "" : "?";
        return "Who coined the phrase " + quoted + skipQ;
      }
      default:
        return "Invalid question type: " + type;
    }
  }
}

// Types where phrases appear as OPTIONS
const PHRASE_OPTION_TYPES: string[] = [
  "origin-include",
  "origin-exclude",
  "date-range-include",
  "date-range-exclude",
  "order",
  "first",
  "last",
  "subcat-include",
  "subcat-exclude",
  "author-include",
  "author-exclude",
];

// Types where phrases appear only as SUBJECT
const PHRASE_SUBJECT_ONLY_TYPES: string[] = [
  "date",
  "origin-multi",
  "subcat-multi",
  "author-origin",
];

type PhraseTypeCounts = {
  correct: number;
  incorrect: number;
};

type PhraseStats = {
  correct: number;
  incorrect: number;
  perType: { [type: string]: PhraseTypeCounts };
  subjectPerType: { [type: string]: number };
};

export const runGameTester = async () => {
  let url = new URL(window.location.href);
  let params = Object.fromEntries(Array(...url.searchParams.entries()));
  if (params.difficulty) {
    let difficulty: string = "easy";
    let questionsCategory: string = "random";
    let numGames: number = 50;
    let questionsArr: Array<QuestionData> = [];
    if (params.difficulty) {
      difficulty = params.difficulty;
    }
    if (params.category) {
      questionsCategory = params.category;
    } else {
      questionsCategory = "random";
    }
    if (params.games) {
      numGames = parseInt(params.games);
    }

    console.log(
      "Building ",
      numGames,
      " test games with ",
      difficulty,
      " difficulty and ",
      questionsCategory,
      " category"
    );
    // If difficulty=all, precompute per-game difficulties (easy/moderate/hard)
    let gameDifficulties: string[] = [];
    if (difficulty === "all") {
      const levels = ["easy", "moderate", "hard"];
      const basePer = Math.floor(numGames / levels.length);
      const remainder = numGames % levels.length;

      levels.forEach((level, index) => {
        let count = basePer + (index < remainder ? 1 : 0);
        for (let k = 0; k < count; k++) {
          gameDifficulties.push(level);
        }
      });
    }

    const dateTime = formatDateTime();
    const baseFileName = `games_${difficulty}_${questionsCategory}_${dateTime}`;

    let csvContent: string = "";

    // Create a second file with question type counts
    let questionTypes: Set<string> = new Set();
    let typeCountContent: string = "Game";

    let allGamesQuestionsArr: Array<QuestionData[]> = [];
    // phrase stats across all games
    let phraseStats: {
      [phrase: string]: PhraseStats;
    } = {};

    const QUESTIONS_PER_GAME = 10;
    const GAP_COLS = 3;

    // per-game load times and aggregates
    let loadTimes: number[] = [];
    let overallLoadTimeTotal = 0;
    let overallLoadTimeCount = 0;
    const loadTimeTotalsByDifficulty: {
      [difficulty: string]: { sum: number; count: number };
    } = {};

    for (let i = 1; i <= numGames; i++) {
      const gameDifficulty =
        difficulty === "all" ? gameDifficulties[i - 1] : difficulty;

      const startTime =
        typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now();

      questionsArr = await getQuestionsArr(
        "",
        QUESTIONS_PER_GAME,
        gameDifficulty,
        questionsCategory,
        []
      );

      const endTime =
        typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now();
      const loadMs = endTime - startTime;

      loadTimes[i - 1] = loadMs;
      overallLoadTimeTotal += loadMs;
      overallLoadTimeCount += 1;

      if (!loadTimeTotalsByDifficulty[gameDifficulty]) {
        loadTimeTotalsByDifficulty[gameDifficulty] = { sum: 0, count: 0 };
      }
      loadTimeTotalsByDifficulty[gameDifficulty].sum += loadMs;
      loadTimeTotalsByDifficulty[gameDifficulty].count += 1;

      allGamesQuestionsArr.push(questionsArr);
      questionsArr.forEach((question) => questionTypes.add(question.type));
      console.log(
        "built " +
          i +
          "/" +
          numGames +
          " TEST GAMES (difficulty: " +
          gameDifficulty +
          ", load time ms: " +
          loadMs.toFixed(2) +
          ")"
      );
    }

    // Use an ordered list of question types
    const orderedQuestionTypes: string[] = Array.from(questionTypes);

    orderedQuestionTypes.forEach((type) => {
      typeCountContent += `,${type}`;
    });
    typeCountContent += "\n";

    const FIELDS_PER_QUESTION = params.showcat ? 4 : 3;
    const BASE_COLS = 1 + QUESTIONS_PER_GAME * FIELDS_PER_QUESTION;
    const loadTimeColIndex = BASE_COLS;

    // Global totals per question type
    const globalTypeTotals: { [type: string]: number } = {};
    orderedQuestionTypes.forEach((t) => {
      globalTypeTotals[t] = 0;
    });

    // Build header row for first CSV
    const headerFields: string[] = [];
    headerFields.push("Game");
    for (let q = 1; q <= QUESTIONS_PER_GAME; q++) {
      headerFields.push(`question ${q}`);
      if (params.showcat) {
        headerFields.push(`question ${q} type`);
      }
      headerFields.push(`answer set ${q}`);
      headerFields.push(`correct answer ${q}`);
    }
    // Gap between game data and type counts
    headerFields.push("Load Time (ms)");
    for (let g = 1; g < GAP_COLS; g++) {
      headerFields.push("");
    }
    orderedQuestionTypes.forEach((type) => {
      headerFields.push(type);
    });
    csvContent += headerFields.map(csvEscape).join(",") + "\n";

    for (let i = 1; i <= numGames; i++) {
      console.log("building game " + i);
      const gameDifficulty =
        difficulty === "all" ? gameDifficulties[i - 1] : difficulty;

      const rowFields: string[] = [];
      rowFields.push("game " + i);

      let typeCounts: { [key: string]: number } = {};
      questionsArr = allGamesQuestionsArr[i - 1];

      questionsArr.forEach((question) => {
        if (!typeCounts[question.type]) {
          typeCounts[question.type] = 0;
        }
        typeCounts[question.type] += 1;
      });

      // update phraseStats for this game's questions
      questionsArr.forEach((question) => {
        // 1) Subject-based counts for subject-only types
        if (
          PHRASE_SUBJECT_ONLY_TYPES.includes(question.type) &&
          question.subject
        ) {
          const subjPhrase = normalizePhrase(question.subject);
          if (subjPhrase) {
            if (!phraseStats[subjPhrase]) {
              phraseStats[subjPhrase] = {
                correct: 0,
                incorrect: 0,
                perType: {},
                subjectPerType: {},
              };
            }
            const subjEntry = phraseStats[subjPhrase];
            if (!subjEntry.subjectPerType[question.type]) {
              subjEntry.subjectPerType[question.type] = 0;
            }
            subjEntry.subjectPerType[question.type] += 1;
          }
        }

        // 2) Option-based counts for all types
        const optionsRaw = (question.options || []).filter(
          (opt) => opt !== undefined
        ) as string[];

        // normalize options and correct answers so phrase text lines up with DB list
        const options = optionsRaw.map((opt) => normalizePhrase(opt));

        let correctAnswers: string[] = [];
        if (typeof question.answer === "string") {
          correctAnswers = [question.answer];
        } else if (Array.isArray(question.answer)) {
          correctAnswers = question.answer as string[];
        }

        const normalizedCorrectAnswers = correctAnswers.map((a) =>
          normalizePhrase(a)
        );
        const correctSet = new Set(normalizedCorrectAnswers);

        options.forEach((opt) => {
          const key = opt;
          if (!phraseStats[key]) {
            phraseStats[key] = {
              correct: 0,
              incorrect: 0,
              perType: {},
              subjectPerType: {},
            };
          }
          const phraseEntry = phraseStats[key];

          if (!phraseEntry.perType[question.type]) {
            phraseEntry.perType[question.type] = {
              correct: 0,
              incorrect: 0,
            };
          }

          const typeEntry = phraseEntry.perType[question.type];

          if (correctSet.has(opt)) {
            phraseEntry.correct += 1;
            typeEntry.correct += 1;
          } else {
            phraseEntry.incorrect += 1;
            typeEntry.incorrect += 1;
          }
        });
      });

      for (let j = 0; j < QUESTIONS_PER_GAME; j++) {
        const question = questionsArr[j];
        if (question) {
          const options = (question.options || []).filter(
            (option) => option !== undefined
          ) as string[];
          let answer: string | string[] | undefined = question.answer;

          if (Array.isArray(answer)) {
            answer = (answer as string[]).join(", ");
          }

          rowFields.push(
            getQuestionText(questionsArr, gameDifficulty, j)
          );
          if (params.showcat) {
            rowFields.push(question.type);
          }
          rowFields.push(options.join(" | "));
          rowFields.push(answer ?? "");
        } else {
          rowFields.push("");
          if (params.showcat) {
            rowFields.push("");
          }
          rowFields.push("");
          rowFields.push("");
        }
      }

      // Gap columns: load time first, then remaining empties
      const loadTime = loadTimes[i - 1] || 0;
      rowFields.push(loadTime.toFixed(2));
      for (let g = 1; g < GAP_COLS; g++) {
        rowFields.push("");
      }

      // Per-type counts for this game (and accumulate global totals)
      orderedQuestionTypes.forEach((type) => {
        const count = typeCounts[type] || 0;
        rowFields.push(count.toString());
        globalTypeTotals[type] += count;
      });

      csvContent += rowFields.map(csvEscape).join(",") + "\n";

      // Second file: per-game type counts
      typeCountContent += `Game ${i}`;
      orderedQuestionTypes.forEach((type) => {
        typeCountContent += `,${typeCounts[type] || 0}`;
      });
      typeCountContent += "\n";
    }

    const totalColumns = headerFields.length;

    // Totals row at the bottom of the first CSV
    const totalsFields: string[] = [];
    for (let i = 0; i < BASE_COLS; i++) {
      totalsFields.push("");
    }
    totalsFields.push("Totals");
    for (let g = 1; g < GAP_COLS; g++) {
      totalsFields.push("");
    }
    orderedQuestionTypes.forEach((type) => {
      totalsFields.push(globalTypeTotals[type].toString());
    });
    csvContent += totalsFields.map(csvEscape).join(",") + "\n";

    // Average load time across all games
    if (overallLoadTimeCount > 0) {
      const avgAll = overallLoadTimeTotal / overallLoadTimeCount;
      const avgAllRow: string[] = [];
      for (let i = 0; i < totalColumns; i++) {
        avgAllRow.push("");
      }
      avgAllRow[0] = "Avg Load Time (all games, ms)";
      avgAllRow[loadTimeColIndex] = avgAll.toFixed(2);
      csvContent += avgAllRow.map(csvEscape).join(",") + "\n";
    }

    // Average load times per difficulty if we ran all three
    if (difficulty === "all") {
      const levels = ["easy", "moderate", "hard"];
      levels.forEach((level) => {
        const stats = loadTimeTotalsByDifficulty[level];
        if (stats && stats.count > 0) {
          const avg = stats.sum / stats.count;
          const row: string[] = [];
          for (let i = 0; i < totalColumns; i++) {
            row.push("");
          }
          row[0] = `Avg Load Time (${level}, ms)`;
          row[loadTimeColIndex] = avg.toFixed(2);
          csvContent += row.map(csvEscape).join(",") + "\n";
        }
      });
    }

    // Get the master phrase list dynamically from the DB
    const { phrases: allPhrases, metadataByPhrase } = await fetchAllPhrasesFromDB();

    // Build phrase usage CSV based on the dynamic phrase list
    let phraseCsv = "Phrase,Incorrect Options,Correct Options";

    PHRASE_METADATA_COLUMNS.forEach((col) => {
      phraseCsv += `,${col.header}`;
    });

    // Hard-coded per-type columns: option-based types (Incorrect + Correct)
    PHRASE_OPTION_TYPES.forEach((type) => {
      phraseCsv += `,${type} Incorrect,${type} Correct`;
    });

    // Hard-coded per-type columns: subject-only types (single subject count)
    PHRASE_SUBJECT_ONLY_TYPES.forEach((type) => {
      phraseCsv += `,${type} Subject`;
    });

    phraseCsv += "\n";

    allPhrases.forEach((phrase) => {
      const stats =
        phraseStats[phrase] || {
          correct: 0,
          incorrect: 0,
          perType: {},
          subjectPerType: {},
        };
      const safeTitle = phrase.replace(/"/g, '""');

      let row = `"${safeTitle}",${stats.incorrect},${stats.correct}`;

      //columns D–J (phrase data from DB)
      const meta = metadataByPhrase[phrase];
      PHRASE_METADATA_COLUMNS.forEach((col) => {
        let value = "";
        if (meta && (meta as any)[col.field] != null) {
          value = String((meta as any)[col.field]);
        }
        const escaped = value.replace(/"/g, '""');
        row += `,"${escaped}"`;
      });

      // Option-based type columns
      PHRASE_OPTION_TYPES.forEach((type) => {
        const typeStats = stats.perType[type] || { incorrect: 0, correct: 0 };
        row += `,${typeStats.incorrect},${typeStats.correct}`;
      });

      // Subject-only type columns
      PHRASE_SUBJECT_ONLY_TYPES.forEach((type) => {
        const subjectCount = stats.subjectPerType[type] || 0;
        row += `,${subjectCount}`;
      });

      phraseCsv += row + "\n";
    });

    // downloads:
    downloadCSV(csvContent, `${baseFileName}.csv`);
    downloadText(typeCountContent, `${baseFileName}_type_counts.csv`);
    downloadCSV(phraseCsv, `${baseFileName}_phrase_option_counts.csv`);
  }
};